import React, { useState, useEffect, useCallback } from 'react';
import './UGStudentDashboard.css';
import collegeConfig from '../../config/collegeConfig';
import { FaChevronLeft, FaUniversity, FaTimes, FaCheckCircle } from 'react-icons/fa';

const UGFoundationCourse = ({ onBack }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalModule, setModalModule] = useState(null);
  const [form, setForm] = useState({ status: 'in_progress', attendancePercent: '', activitiesCompleted: '', reflectionNotes: '' });
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchModules = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/foundation-course`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setModules(data.modules || []);
    } catch (err) {
      console.error('Error fetching Foundation Course modules:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const openModal = (m) => {
    setModalModule(m);
    setForm({
      status: m.status && m.status !== 'not_started' ? m.status : 'in_progress',
      attendancePercent: m.attendancePercent ?? '',
      activitiesCompleted: m.activitiesCompleted || '',
      reflectionNotes: m.reflectionNotes || ''
    });
  };

  const submit = async () => {
    if (form.attendancePercent !== '' && (Number(form.attendancePercent) < 0 || Number(form.attendancePercent) > 100)) {
      alert('Attendance % must be between 0 and 100.');
      return;
    }
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/foundation-course/${modalModule.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          ...form,
          attendancePercent: form.attendancePercent === '' ? null : Number(form.attendancePercent)
        })
      });
      const data = await res.json();
      if (data.success) {
        setModalModule(null);
        await fetchModules();
      } else {
        alert(data.message || 'Failed to save progress');
      }
    } catch (err) {
      console.error('Error saving Foundation Course progress:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Explicit resubmit after a "needs revision" decision — editing the
  // module's progress alone does not flip its review status back to pending.
  const resubmit = async (moduleId) => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/foundation-course/${moduleId}/resubmit`, { method: 'PUT', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchModules();
      else alert(data.message || 'Failed to resubmit module');
    } catch (err) {
      console.error('Error resubmitting Foundation Course module:', err);
    }
  };

  const completedCount = modules.filter(m => m.status === 'completed').length;

  return (
    <main className="ug-main">
      <button className="ug-back-btn" onClick={onBack}><FaChevronLeft /> Back to Dashboard</button>
      <h2 className="ug-section-title"><FaUniversity /> Foundation Course</h2>
      <p className="ug-modal-hint" style={{ margin: '0 0 14px 0' }}>
        The mandatory one-time orientation at the start of Phase 1 (NMC Foundation Course, 2019). Completion is attendance-based (&ge;75% required) and certified by the Principal/Dean — track your attendance and notes per module below.
      </p>

      {loading ? (
        <div className="ug-due-empty">Loading Foundation Course modules...</div>
      ) : (
        <div className="ug-topic-block">
          <div className="ug-topic-name">
            Modules <span style={{ float: 'right', textTransform: 'none', fontWeight: 400 }}>{completedCount}/{modules.length} completed</span>
          </div>
          {modules.map(m => (
            <div key={m.id} className="ug-aetcom-module">
              <div className="ug-competency-row">
                <div className="ug-competency-main">
                  <span className="ug-competency-text">{m.title}</span>
                </div>
                <div className="ug-competency-side">
                  {m.attendancePercent !== null && m.attendancePercent !== undefined && (
                    <span className="ug-status-badge ug-status-not-started">{m.attendancePercent}% attendance</span>
                  )}
                  {m.status === 'completed' ? (
                    <span className="ug-status-badge ug-status-approved"><FaCheckCircle /> Completed</span>
                  ) : m.status === 'in_progress' ? (
                    <span className="ug-status-badge ug-status-pending">In Progress</span>
                  ) : (
                    <span className="ug-status-badge ug-status-not-started">Not Started</span>
                  )}
                  {m.review?.status === 'approved' && (
                    <span className="ug-status-badge ug-status-approved"><FaCheckCircle /> Approved</span>
                  )}
                  {m.review?.status === 'needs_revision' && (
                    <span className="ug-status-badge ug-status-revision">Needs revision</span>
                  )}
                </div>
              </div>
              <p className="ug-aetcom-module-hint">{m.description}</p>
              <p className="ug-aetcom-module-hint">
                {m.activitiesCompleted || m.reflectionNotes
                  ? (m.activitiesCompleted || m.reflectionNotes)
                  : 'No progress recorded yet. Click "Update progress" to add notes.'}
              </p>
              {m.review?.feedback && (
                <div className="ug-aetcom-faculty-feedback">
                  <strong>{m.review.status === 'needs_revision' ? 'Needs revision — ' : ''}Faculty feedback:</strong> {m.review.feedback}
                </div>
              )}
              {m.review?.reviewerName && (
                <p className="ug-reviewer-tag">
                  {m.review.status === 'approved' ? 'Verified by' : 'Reviewed by'} {m.review.reviewerName}
                </p>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                {m.review?.status !== 'approved' && (
                  <button className="ug-log-btn" onClick={() => openModal(m)}>Update progress</button>
                )}
                {m.review?.status === 'needs_revision' && (
                  <button className="ug-log-btn" onClick={() => resubmit(m.id)}>Resubmit for review</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalModule && (
        <div className="ug-modal-overlay" onClick={() => !submitting && setModalModule(null)}>
          <div className="ug-modal" onClick={e => e.stopPropagation()}>
            <div className="ug-modal-head">
              <h3>Update progress</h3>
              <FaTimes onClick={() => !submitting && setModalModule(null)} />
            </div>
            <p className="ug-modal-competency">{modalModule.title}</p>

            <label className="ug-form-label">Status</label>
            <select
              className="ug-form-input"
              value={form.status}
              onChange={e => setForm({ ...form, status: e.target.value })}
            >
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>

            <label className="ug-form-label">Attendance % (optional)</label>
            <input
              type="number"
              min="0"
              max="100"
              className="ug-form-input"
              value={form.attendancePercent}
              onChange={e => setForm({ ...form, attendancePercent: e.target.value })}
            />

            <label className="ug-form-label">Activities completed</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={form.activitiesCompleted}
              onChange={e => setForm({ ...form, activitiesCompleted: e.target.value })}
            />

            <label className="ug-form-label">Reflection notes</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={form.reflectionNotes}
              onChange={e => setForm({ ...form, reflectionNotes: e.target.value })}
            />

            <label className="ug-form-label">Faculty feedback</label>
            <div className="ug-aetcom-faculty-feedback">
              {modalModule.review?.feedback || modalModule.facultyFeedback || 'Faculty feedback is filled by faculty only.'}
            </div>
            {modalModule.review?.reviewerName && (
              <p className="ug-reviewer-tag">
                {modalModule.review.status === 'approved' ? 'Verified by' : 'Reviewed by'} {modalModule.review.reviewerName}
              </p>
            )}

            <div className="ug-modal-actions">
              <button className="ug-btn-secondary" onClick={() => setModalModule(null)} disabled={submitting}>Cancel</button>
              <button className="ug-btn-primary" onClick={submit} disabled={submitting}>
                {submitting ? 'Saving...' : 'Save progress'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default UGFoundationCourse;
